import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface MerchantOption {
  id: string;
  shopify_store_domain: string | null;
  store_name: string | null;
}

interface StoreApiLogRow {
  id: string;
  merchant_id: string | null;
  store_name: string | null;
  store_domain: string | null;
  platform_type: string | null;
  function_name: string;
  api_name: string | null;
  severity: string;
  status_code: number | null;
  error_code: string | null;
  error_message: string | null;
  reason: string | null;
  request_payload: unknown;
  response_payload: unknown;
  metadata: unknown;
  created_at: string;
}

export function StoreLogManager({ merchants }: { merchants: MerchantOption[] }) {
  const [logs, setLogs] = useState<StoreApiLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const fetchLogs = async () => {
    try {
      const query = supabase
        .from("store_api_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      const { data, error } = selectedMerchantId === "all"
        ? await query
        : await query.eq("merchant_id", selectedMerchantId);

      if (error) {
        throw error;
      }

      setLogs((data || []) as StoreApiLogRow[]);
    } catch (error) {
      console.error("Failed to fetch store logs:", error);
      toast.error("Failed to load store logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchLogs();
  }, [selectedMerchantId]);

  const filteredLogs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return logs;

    return logs.filter((log) =>
      [
        log.store_name,
        log.store_domain,
        log.function_name,
        log.api_name,
        log.error_message,
        log.reason,
        log.error_code,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [logs, search]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchLogs();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Store API Logs
            </CardTitle>
            <CardDescription>
              Per-store failure logs with function name, API name, request, response, and reason.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="md:w-64">
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={selectedMerchantId}
              onChange={(event) => setSelectedMerchantId(event.target.value)}
            >
              <option value="all">All stores</option>
              {merchants.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.store_name || merchant.shopify_store_domain || merchant.id}
                </option>
              ))}
            </select>
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by store, function, API, error, or reason"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading logs...</div>
        ) : filteredLogs.length === 0 ? (
          <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
            No store API logs found yet.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => {
              const expanded = expandedLogId === log.id;
              return (
                <div key={log.id} className="rounded-lg border">
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-4 p-4 text-left"
                    onClick={() => setExpandedLogId(expanded ? null : log.id)}
                  >
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">
                          {log.store_name || log.store_domain || "Unknown store"}
                        </p>
                        <Badge variant="outline">{log.platform_type || "unknown"}</Badge>
                        <Badge className="bg-red-500/10 text-red-600">{log.severity}</Badge>
                        {log.status_code ? <Badge variant="secondary">HTTP {log.status_code}</Badge> : null}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span>{log.function_name}</span>
                        {log.api_name ? <span> • {log.api_name}</span> : null}
                        <span> • {new Date(log.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {log.error_message || "Unknown error"}
                      </p>
                      {log.reason ? (
                        <p className="text-sm text-muted-foreground">{log.reason}</p>
                      ) : null}
                    </div>
                    {expanded ? <ChevronUp className="mt-1 h-5 w-5 text-muted-foreground" /> : <ChevronDown className="mt-1 h-5 w-5 text-muted-foreground" />}
                  </button>

                  {expanded ? (
                    <div className="grid gap-4 border-t p-4 lg:grid-cols-3">
                      <LogBlock title="Request" value={log.request_payload} />
                      <LogBlock title="Response" value={log.response_payload} />
                      <LogBlock title="Metadata" value={log.metadata} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LogBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
        {toPrettyJson(value)}
      </pre>
    </div>
  );
}

function toPrettyJson(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
